import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faXmark,
  faDatabase,
  faArrowRight,
  faEnvelope,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { resolveFaIcon } from 'helpers/faIcons';
import { formatNumber } from 'helpers/numberFormat';
import { useTranslation } from 'react-i18next';

// Floating selection action row that mounts when one or more rows are
// selected. Lives in the table's control strip ABOVE the card (CgTable's
// `selectionDock` slot) so the bulk verbs stay visible next to the other
// table controls instead of scrolling off-screen below a long table.
// Styled on the same white/slate/teal vocabulary as the Radix controls.

const Action = ({ icon, label, onClick, tone = 'default', disabled = false }) => {
  const toneClass = disabled
    ? 'border-slate-200 text-slate-300 cursor-not-allowed'
    : tone === 'danger'
      ? 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
      : 'border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border bg-white shadow-sm ${toneClass} transition-colors`}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          className="text-xs"
        />
      )}
      {label}
    </button>
  );
};

export default function BulkActionDock({
  selectedCount,
  onDelete,
  onAddEmissionsData,
  onChase,
  onConnect,
  onClear,
  canAddEmissionsData,
  canChase,
  canConnect,
  extraActions,
  // Generic verb list for tables without bespoke wiring — each entry is
  // { label, icon (fa name or icon object), tone, onClick, disabled }.
  // Rendered before the built-in verbs; used by CgTable's automatic dock.
  actions = null,
}) {
  const { t } = useTranslation('common');
  if (!selectedCount) return null;

  return (
    <div
      data-testid="bulk-action-dock"
      className="flex items-center justify-between gap-3 px-3.5 py-2 bg-white border border-slate-200 rounded-xl shadow-sm"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-primaryColor/10 text-primaryColor">
          {t('bulk.selectedCount', {
            count: selectedCount,
            formattedCount: formatNumber(selectedCount),
            defaultValue: '{{formattedCount}} selected',
            defaultValue_other: '{{formattedCount}} selected',
          })}
        </span>
        {actions?.map((action, idx) => (
          <Action
            key={`dock-action-${idx}`}
            icon={typeof action.icon === 'string' ? resolveFaIcon(action.icon) : action.icon}
            label={action.label}
            tone={action.tone}
            onClick={action.onClick}
            disabled={action.disabled}
          />
        ))}
        {canChase && (
          <Action
            icon={faEnvelope}
            label={t('bulk.chase', 'Chase')}
            onClick={onChase}
          />
        )}
        {canConnect && (
          <Action
            icon={faLink}
            label={t('bulk.sendConnectionRequest', 'Send connection request')}
            onClick={onConnect}
          />
        )}
        {canAddEmissionsData && (
          <Action
            icon={faDatabase}
            label={t('bulk.addEmissionsData', 'Add emissions data')}
            onClick={onAddEmissionsData}
          />
        )}
        {extraActions}
        {onDelete && (
          <Action
            icon={faTrash}
            label={t('actions.delete', 'Delete')}
            tone="danger"
            onClick={onDelete}
          />
        )}
      </div>
      <button
        type="button"
        onClick={onClear}
        aria-label={t('actions.clearSelection', 'Clear selection')}
        className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded inline-flex items-center gap-1.5 text-xs"
      >
        {t('actions.clear', 'Clear')}
        <FontAwesomeIcon
          icon={faXmark}
          className="text-xs"
        />
      </button>
    </div>
  );
}

// Reusable arrow re-exported for callers that want the same chevron motif
// elsewhere on the page.
export const BulkActionArrow = faArrowRight;
